<template>
  <div class="container-fluid py-4">
    <b-breadcrumb class="mb-3 mt-2">
      <b-breadcrumb-item href="/">
        <i class="fa fa-home" aria-hidden="true"></i> Home
      </b-breadcrumb-item>
    </b-breadcrumb>

    <div class="row mb-4">
      <div class="col-12">
        <h2 class="mb-4">Care Assistants</h2>

        <BButton variant="primary" class="mb-3" @click="openModal">
          <i class="fas fa-plus-circle me-2"></i>Add New Carer
        </BButton>

        <BCard>
          <BCardHeader>
            <h3 class="mb-0">Carers</h3>
          </BCardHeader>
          <BCardBody>
            <!-- Desktop Table -->
            <div class="table-responsive d-none d-md-block">
              <BTable
                striped
                hover
                :items="tableItems"
                :fields="tableFields"
                :busy="loading"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <BSpinner class="align-middle me-2"></BSpinner>
                    <strong>Loading...</strong>
                  </div>
                </template>

                <template #cell(confirmed)="data">
                  <BBadge :variant="data.value ? 'success' : 'warning'">
                    {{ data.value ? "Yes" : "No" }}
                  </BBadge>
                </template>

                <template #cell(status)="data">
                  <BBadge :variant="data.value ? 'danger' : 'success'">
                    {{ data.value ? "Blocked" : "Active" }}
                  </BBadge>
                </template>

                <template #cell(actions)="data">
                  <div class="btn-group btn-group-sm">
                    <BButton
                      variant="outline-primary"
                      @click="editUser(data.item.rawData)"
                    >
                      <i class="fas fa-edit"></i>
                    </BButton>
                    <BButton
                      :variant="
                        data.item.rawData.blocked
                          ? 'outline-success'
                          : 'outline-danger'
                      "
                      @click="toggleBlockUser(data.item.rawData)"
                    >
                      <i
                        :class="
                          data.item.rawData.blocked
                            ? 'fas fa-unlock'
                            : 'fas fa-lock'
                        "
                      ></i>
                    </BButton>
                  </div>
                </template>
              </BTable>
            </div>

            <!-- Mobile Cards -->
            <div v-if="loading" class="d-md-none text-center py-4">
              <BSpinner variant="primary"></BSpinner>
              <p class="mt-2">Loading users...</p>
            </div>

            <div v-else class="row row-cols-1 g-3 d-md-none">
              <div v-for="user in users" :key="user.id" class="col">
                <BCard :border-variant="user.blocked ? 'danger' : null">
                  <BCardBody>
                    <BCardTitle>{{ user.fullname }}</BCardTitle>
                    <BCardSubTitle class="mb-2 text-muted">{{
                      user.email
                    }}</BCardSubTitle>

                    <BListGroup flush class="mb-3">
                      <BListGroupItem class="d-flex justify-content-between">
                        <span>Role:</span>
                        <span>{{ getRoleName(user.role?.id) }}</span>
                      </BListGroupItem>
                      <BListGroupItem class="d-flex justify-content-between">
                        <span>Confirmed:</span>
                        <BBadge
                          :variant="user.confirmed ? 'success' : 'warning'"
                        >
                          {{ user.confirmed ? "Yes" : "No" }}
                        </BBadge>
                      </BListGroupItem>
                      <BListGroupItem class="d-flex justify-content-between">
                        <span>Status:</span>
                        <BBadge :variant="user.blocked ? 'danger' : 'success'">
                          {{ user.blocked ? "Blocked" : "Active" }}
                        </BBadge>
                      </BListGroupItem>
                    </BListGroup>

                    <div class="d-grid gap-2 d-flex justify-content-end">
                      <BButton
                        variant="outline-primary"
                        size="sm"
                        @click="editUser(user)"
                      >
                        <i class="fas fa-edit"></i> Edit
                      </BButton>
                      <BButton
                        :variant="
                          user.blocked ? 'outline-success' : 'outline-danger'
                        "
                        size="sm"
                        @click="toggleBlockUser(user)"
                      >
                        <i
                          :class="
                            user.blocked ? 'fas fa-unlock' : 'fas fa-lock'
                          "
                        ></i>
                        {{ user.blocked ? "Unblock" : "Block" }}
                      </BButton>
                    </div>
                  </BCardBody>
                </BCard>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </div>
    </div>

    <!-- User Form Modal -->
    <BModal
      v-model="showModal"
      :title="editingUser ? 'Edit User' : 'Add New User'"
      size="lg"
      @hidden="resetForm"
      :busy="formLoading"
    >
      <BForm @submit.prevent="submitUser">
        <BRow class="g-3">
          <BCol md="6">
            <BFormGroup label="Fullname" label-for="fullname">
              <BFormSelect id="fullname" v-model="userForm.fullname">
                <BFormSelectOption value="" disabled
                  >Select employee</BFormSelectOption
                >
                <BFormSelectOption
                  v-for="emp in apiEmployees"
                  :key="emp.id"
                  :value="emp.text"
                >
                  {{ emp.text }}
                </BFormSelectOption>
              </BFormSelect>
            </BFormGroup>
          </BCol>

          <BCol md="6">
            <BFormGroup label="Email" label-for="email">
              <BFormInput
                type="email"
                id="email"
                v-model="userForm.email"
                required
                :state="emailState"
              />
              <BFormInvalidFeedback v-if="!emailState">
                Please enter email @mel.me
              </BFormInvalidFeedback>
            </BFormGroup>
          </BCol>

          <BCol md="6">
            <BFormGroup label="Password" label-for="password">
              <BFormInput
                type="password"
                id="password"
                v-model="userForm.password"
                :required="!editingUser"
                :state="passwordState"
              />
              <BFormText v-if="editingUser"
                >Leave blank to keep current password</BFormText
              >
              <BFormInvalidFeedback v-if="!passwordState">
                Password must be at least 6 characters
              </BFormInvalidFeedback>
            </BFormGroup>
          </BCol>

          <BCol md="6">
            <BFormGroup label="Role" label-for="role">
              <BFormSelect id="role" v-model="userForm.role">
                <BFormSelectOption :value="null" disabled
                  >Select a role</BFormSelectOption
                >
                <BFormSelectOption
                  v-for="role in roles"
                  :key="role.id"
                  :value="role.id"
                >
                  {{ role.name }}
                </BFormSelectOption>
              </BFormSelect>
            </BFormGroup>
          </BCol>

          <BCol md="6">
            <BFormGroup>
              <BFormCheckbox id="confirmed" v-model="userForm.confirmed" switch>
                Confirmed
              </BFormCheckbox>
            </BFormGroup>
          </BCol>

          <BCol md="6" v-if="editingUser">
            <BFormGroup>
              <BFormCheckbox id="blocked" v-model="userForm.blocked" switch>
                Blocked
              </BFormCheckbox>
            </BFormGroup>
          </BCol>
          <BCol md="6">
            <BFormGroup :label="'Assign Residents '">
              <div class="form-check" v-for="rs in residents.data" :key="rs.id">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`rs-${rs.id}`"
                  :value="String(rs.id)"
                  v-model="userForm.residents"
                />
                <label class="form-check-label" :for="`rs-${rs.id}`">
                  {{ rs.attributes.firstname }} {{ rs.attributes.lastname }}
                </label>
              </div>
            </BFormGroup>
          </BCol>
        </BRow>
      </BForm>

      <template #footer>
        <BButton
          variant="secondary"
          :disabled="formLoading"
          @click="showModal = false"
        >
          Close
        </BButton>
        <BButton
          variant="primary"
          @click="submitUser"
          :disabled="formLoading || !formValid"
        >
          <span
            v-if="formLoading"
            class="spinner-border spinner-border-sm"
            role="status"
          ></span>
          {{ editingUser ? "Update User" : "Add User" }}
        </BButton>
      </template>
    </BModal>

    <BToast :modelValue="!!toastMessage" @update:modelValue="toastMessage = ''">
      {{ toastMessage }}
    </BToast>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, watchEffect } from "vue";
import axios from "axios";
import { useToast } from "vue-toastification";
import { getBread, getEmployees } from "../components/values";
import { BASE_URL } from "@/config";
import { UPDATE_RESIDENT, GET_RESIDENTS } from "@/hooks/queries";
import { useQuery, useMutation } from "@vue/apollo-composable";

export default {
  name: "UserManagement",

  setup() {
    // API configuration
    const API_URL = BASE_URL;
    const token = localStorage.getItem("jwt");

    const apiEmployees = ref([]);

    // Reactive data
    const users = ref([]);
    const roles = ref([]);
    const userForm = ref({
      id: null,
      email: "",
      fullname: "",
      password: "",
      confirmed: true,
      role: null,
      blocked: false,
      residents: [],
    });
    const editingUser = ref(false);
    const showModal = ref(false);
    const loading = ref(false);
    const formLoading = ref(false);
    const toastMessage = ref("");
    const toast = useToast();

    const { mutate: updateResident } = useMutation(UPDATE_RESIDENT);

    const assignCarersToResidents = async (userId, selectedResidentIds) => {
  // Convert userId to string for consistent comparison
  const userIdStr = String(userId);
  
  console.log("=== assignCarersToResidents DEBUG ===");
  console.log("User ID:", userIdStr);
  console.log("Selected resident IDs:", selectedResidentIds);
  
  for (const resident of residents.value.data) {
    const residentUsers = resident?.attributes?.users_permissions_users?.data || [];
    const residentUserIds = residentUsers.map(u => String(u.id));
    
    const currentlyHasUser = residentUserIds.includes(userIdStr);
    const shouldHaveUser = selectedResidentIds.includes(String(resident.id));
    
    console.log(`Resident ${resident.id}: hasUser=${currentlyHasUser}, shouldHave=${shouldHaveUser}`);
    
    try {
      if (shouldHaveUser && !currentlyHasUser) {
        // Add user to resident
        console.log(`Adding user ${userIdStr} to resident ${resident.id}`);
        const updatedUsers = [
          ...residentUsers.map((u) => u.id),
          Number(userId), // Convert back to number for API if needed
        ];
        
        await updateResident({
          id: resident.id,
          data: { users_permissions_users: updatedUsers },
        });
        console.log(`Successfully added user to resident ${resident.id}`);
      }
      else if (!shouldHaveUser && currentlyHasUser) {
        // Remove user from resident
        console.log(`Removing user ${userIdStr} from resident ${resident.id}`);
        const updatedUsers = residentUsers
          .filter((u) => String(u.id) !== userIdStr)
          .map((u) => u.id);
        
        await updateResident({
          id: resident.id,
          data: { users_permissions_users: updatedUsers },
        });
        console.log(`Successfully removed user from resident ${resident.id}`);
      }
      else {
        console.log(`No change needed for resident ${resident.id}`);
      }
    } catch (error) {
      console.error(`Error updating resident ${resident.id}:`, error);
    }
  }
  
  // Refresh the residents data to reflect changes
  await refetchResidents();
  console.log("Residents data refreshed");
};



    const residents = ref([]);

    const { result: residentsResult, refetch: refetchResidents } =
      useQuery(GET_RESIDENTS);
    watchEffect(() => {
      if (residentsResult.value?.residents) {
        residents.value = residentsResult.value.residents;
        //console.log('Loaded residents:', residents.value.data);
      }
    });

    // Table fields
    const tableFields = [
      { key: "id", label: "ID" },
      { key: "fullname", label: "Fullname" },
      { key: "email", label: "Email" },
      {
        key: "role",
        label: "Role",
        formatter: (value, key, item) => getRoleName(item.rawData.role?.id),
      },
      { key: "confirmed", label: "Confirmed" },
      {
        key: "status",
        label: "Status",
        formatter: (value, key, item) => item.rawData.blocked,
      },
      { key: "actions", label: "Actions" },
    ];

    // Table items
    const tableItems = computed(() => {
      return users.value.map((user) => ({
        ...user,
        rawData: user, // Store the full user object for access in slots
      }));
    });

    // Form validation
    const fullnameState = computed(() => userForm.value.fullname.length > 0);
    const emailState = computed(() =>
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.value.email)
    );
    const passwordState = computed(() => {
      if (editingUser.value && !userForm.value.password) return true;
      return userForm.value.password.length >= 6;
    });
    const roleState = computed(() => userForm.value.role !== null);
    const formValid = computed(() => {
      return (
        fullnameState.value &&
        emailState.value &&
        passwordState.value &&
        roleState.value
      );
    });

    // Fetch all users with blocked status
    const fetchUsers = async () => {
      try {
        loading.value = true;
        const response = await axios.get(`${API_URL}/api/users?populate=role`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        //users.value = response.data;
        // Filter users by email domain @mel.me
        users.value = response.data.filter(
          (user) => user.email && user.email.endsWith("@mel.me")
        );
      } catch (error) {
        showError("Failed to fetch users", error);
      } finally {
        loading.value = false;
      }
    };

    // Fetch all roles
    const fetchRoles = async () => {
      const allowedTypes = ["carers", "owners", "viewers"];
      try {
        const response = await axios.get(
          `${API_URL}/api/users-permissions/roles`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        //roles.value = response.data.roles.filter(role => role.type !== 'public');
        //roles.value = response.data.roles.filter(role => role.name === 'carers' );
        roles.value = response.data.roles.filter((role) =>
          allowedTypes.includes(role.name)
        );
        //console.log(response.data.roles);
      } catch (error) {
        showError("Failed to fetch roles", error);
      }
    };

    // Add or update user
    const submitUser = async () => {
      if (!formValid.value) return;

      try {
        formLoading.value = true;
        const userData = {
          fullname: userForm.value.fullname,
          email: userForm.value.email,
          username: userForm.value.email,
          confirmed: userForm.value.confirmed,
          role: userForm.value.role,
          empnumber: getEmployeeID(userForm.value.fullname),
          blocked: userForm.value.blocked,
        };

        if (userForm.value.password) {
          userData.password = userForm.value.password;
        }
        let newUserId = null;

        if (editingUser.value) {
          await axios.put(
            `${API_URL}/api/users/${userForm.value.id}`,
            userData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          // Assign carer to selected resident

          if (userForm.value.residents && userForm.value.id) {
            await assignCarersToResidents(
              userForm.value.id,
              userForm.value.residents
            );
          }
          await refetchResidents();

          toast.success("User updated successfully");
        } else {
          const res = await axios.post(
            `${API_URL}/api/auth/local/register`,
            {
              ...userData,
              role: userForm.value.role,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          newUserId = res.data.user.id;
          console.log("New user " + newUserId);

          // Assign carer to selected  resident
          if (userForm.value.residents && userForm.value.id) {
            await assignCarersToResidents(
              userForm.value.id,
              userForm.value.residents
            );
          }
          await refetchResidents();

          toast.success("User created successfully");
        }

        // Assign carer to selected resident

        if (userForm.value.residents && newUserId) {
          await assignCarersToResidents(newUserId, userForm.value.residents);
        }

        showModal.value = false;
        fetchUsers();
      } catch (error) {
        showError("Failed to save user", error);
      } finally {
        formLoading.value = false;
      }
    };

    const findUserResidentIds = (userId) => {
      //console.log("=== findUserResidentIds DEBUG ===");
      //console.log("User ID:", userId, "Type:", typeof userId);

      if (!residents.value.data || residents.value.data.length === 0) {
        //console.log("No residents data available");
        return [];
      }

      const residentIds = residents.value.data
        .filter((rs) => {
          const residentUsers =
            rs?.attributes?.users_permissions_users?.data || [];
          const userIds = residentUsers.map((u) => u.id);
          //console.log(`Resident ${rs.id} has users:`, userIds);

          // Convert both to string for comparison to handle type differences
          const hasUser = userIds.some((uId) => String(uId) === String(userId));
          //console.log(`Resident ${rs.id} has user ${userId}:`, hasUser);
          return hasUser;
        })
        .map((rs) => String(rs.id)); // Ensure we return strings for checkbox values

      //console.log("Found resident IDs:", residentIds);
      return residentIds;
    };
    
    watch(
    () => userForm.value.residents,
    (newVal) => {
    console.log("Selected residents:", newVal)
     }
    );

    // Edit user
    const editUser = (user) => {
      userForm.value = {
        id: user.id,
        fullname: user.fullname,
        email: user.email,
        password: "",
        confirmed: user.confirmed,
        role: user.role?.id || null,
        blocked: user.blocked || false,
        residents: findUserResidentIds(user.id),
      };
      editingUser.value = true;
      showModal.value = true;
    };

    // Toggle user block status
    const toggleBlockUser = async (user) => {
      const action = user.blocked ? "unblock" : "block";
      if (!confirm(`Are you sure you want to ${action} ${user.fullname}?`))
        return;

      try {
        await axios.put(
          `${API_URL}/api/users/${user.id}`,
          {
            blocked: !user.blocked,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success(`User ${action}ed successfully`);
        fetchUsers();
      } catch (error) {
        showError(`Failed to ${action} user`, error);
      }
    };

    // Open modal for new user
    const openModal = () => {
      resetForm();
      showModal.value = true;
    };

    // Reset form
    const resetForm = () => {
      userForm.value = {
        id: null,
        fullname: "",
        email: "",
        password: "",
        confirmed: true,
        role: null,
        blocked: false,
        residents: [],
      };
      editingUser.value = false;
    };

    // Get role name by ID
    const getRoleName = (roleId) => {
      if (!roleId) return "None";
      const foundRole = roles.value.find((r) => r.id === roleId);
      return foundRole ? foundRole.name : "Unknown";
    };

    // Show error message
    const showError = (message, error) => {
      console.error(message, error);
      const errorMessage =
        error.response?.data?.error?.message || error.message;
      toast.error(`${message}: ${errorMessage}`);
    };
    const getEmployeeID = (name) => {
      const option = apiEmployees.value.find(
        (option) => option.text.toLowerCase() === name.toLowerCase()
      );
      return option ? option.value : "";
    };

    const fetchEmployees = async () => {
      //loading.value = true;
      try {
        const emps = await getEmployees();
        apiEmployees.value = emps;
        //console.log(apiEmployees.value);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        //loading.value = false;
      }
    };

    // Initialize component
    onMounted(() => {
      fetchUsers();
      fetchRoles();
      fetchEmployees();
    });

    return {
      users,
      roles,
      getEmployees,
      getEmployeeID,
      fetchEmployees,
      apiEmployees,
      userForm,
      editingUser,
      showModal,
      loading,
      formLoading,
      tableFields,
      tableItems,
      fullnameState,
      emailState,
      passwordState,
      roleState,
      formValid,
      toastMessage,
      getBread,
      openModal,
      submitUser,
      editUser,
      toggleBlockUser,
      resetForm,
      getRoleName,
      residents,
    };
  },
};
</script>

<style scoped>
.container-fluid {
  max-width: 80%;
}

.table-responsive {
  overflow-x: auto;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

@media (max-width: 767.98px) {
  .btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
}
</style>
